import { clearViewAsCustomer, isPreviewCustomer, type ViewAsCustomer } from '@/lib/view-as';

export interface ViewAsBannerProps {
  /** The customer whose My Events is on screen. */
  readonly customer: ViewAsCustomer;
}

/**
 * The persistent banner a BCE staff member sees while viewing a customer's My
 * Events.
 *
 * Always visible, never dismissible. The whole risk of impersonation is
 * forgetting you are in it — reading a customer's events and believing they
 * are your own, or worse, describing them to somebody else as though they were
 * general. Saying plainly whose data this is, on every page, is what makes the
 * feature safe to use rather than merely safe to build.
 *
 * There is no "act as" elevation here, unlike the client portal's equivalent:
 * the API refuses any write while impersonating, so offering a control that
 * appeared to enable one would be a lie.
 *
 * @param props - The customer being viewed.
 * @returns The banner.
 */
export function ViewAsBanner({ customer }: ViewAsBannerProps): React.ReactElement {
  /** Leaves the customer's view; the gate then signposts back to BCEcloud. */
  function stop(): void {
    clearViewAsCustomer();
    // Reload rather than navigate: everything on screen was fetched as the
    // customer, and none of it should survive leaving their view.
    window.location.assign('/');
  }

  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-300 bg-amber-100 px-5 py-2 text-portal-small text-amber-900"
    >
      <span aria-hidden="true">⚑</span>
      <span>
        You are viewing My Events as <b>{customer.name}</b> ({customer.email}) &mdash;{' '}
        <b>read-only</b>.
        {isPreviewCustomer(customer)
          ? ' They have no account yet — this is what they will see once they first sign in.'
          : ''}
      </span>
      <button
        type="button"
        onClick={stop}
        className="ml-auto font-bold underline underline-offset-2"
      >
        Stop viewing
      </button>
    </div>
  );
}
